import type { QueryClient } from '@tanstack/react-query';
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { createRootRouteWithContext, Outlet } from '@tanstack/react-router';
import { useEffect } from 'react';
// import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { MuiAlert, MuiBox, MuiButton, MuiCssBaseline, MuiThemeProvider, MuiTypography } from '@/components/mui';
import { RouterTracker } from '@/components/Tracking/RouterTracker';
import { ErrorToast } from '@/components/UI/ErrorToast';
import { config } from '@/config/env';
import { useThemeStore } from '@/stores/themeStore';
import { getTheme } from '@/theme/muiTheme';

interface MyRouterContext {
  queryClient: QueryClient;
}

function RootComponent() {
  const isDark = useThemeStore((state) => state.isDark);
  const theme = getTheme(isDark);

  return (
    <MuiThemeProvider theme={theme}>
      <MuiCssBaseline />
      <RouterTracker />
      <Outlet />
      <ErrorToast />
      {/* {config.debug && <ReactQueryDevtools initialIsOpen={false} />} */}
      {/* {config.debug && <TanStackRouterDevtools />} */}
    </MuiThemeProvider>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  const isDark = useThemeStore((state) => state.isDark);
  const theme = getTheme(isDark);

  useEffect(() => {
    // Report to Sentry
    if (error && window.Sentry) {
      window.Sentry.captureException(error);
    }
  }, [error]);

  return (
    <MuiThemeProvider theme={theme}>
      <MuiCssBaseline />
      <MuiBox
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          p: 3,
          textAlign: 'center',
        }}
      >
        <MuiAlert severity="error" sx={{ mb: 3, maxWidth: 600 }}>
          <MuiTypography variant="h6" gutterBottom>
            Something went wrong
          </MuiTypography>
          <MuiTypography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {error.message || 'An unexpected error occurred'}
          </MuiTypography>
        </MuiAlert>

        <MuiBox sx={{ display: 'flex', gap: 2 }}>
          <MuiButton variant="contained" onClick={reset}>
            Try Again
          </MuiButton>
          <MuiButton variant="outlined" onClick={() => window.location.reload()}>
            Reload Page
          </MuiButton>
        </MuiBox>

        {config.debug && (
          <MuiBox sx={{ mt: 3, maxWidth: 800, textAlign: 'left' }}>
            <MuiTypography variant="caption" color="text.secondary">
              Error details (debug mode):
            </MuiTypography>
            <MuiBox
              component="pre"
              sx={{
                mt: 1,
                p: 2,
                backgroundColor: 'background.paper',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                fontSize: '0.75rem',
                overflow: 'auto',
                maxHeight: 200,
              }}
            >
              {error.stack}
            </MuiBox>
          </MuiBox>
        )}
      </MuiBox>
    </MuiThemeProvider>
  );
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
  component: RootComponent,
  errorComponent: ErrorComponent,
});
